import { Component, OnInit, OnDestroy } from "@angular/core";
import { GuidedTourService } from "../guided-tour.service";
import { Subscription } from "rxjs";
import { Constants } from "./guided-tour.constants";
import { ModalService } from "src/app/shared/modal.service";

@Component({
  selector: "app-guided-tour",
  templateUrl: "./guided-tour.component.html",
  styleUrls: ["./guided-tour.component.scss"],
})
export class GuidedTourComponent implements OnInit, OnDestroy {
  constants = Constants;
  showBackdrop = false;
  steps = [];
  currentStep = this.constants.NullStep;
  totalStepCount: number;

  private $currentStep: Subscription;
  private $guidedTourCompleted: Subscription;

  constructor(
    private tourService: GuidedTourService,
    private modalService: ModalService
  ) {}

  ngOnInit(): void {
    this.initializeSteps();

    this.$currentStep = this.tourService.currentStep.subscribe((response) => {
      if (response !== this.currentStep.stepCount) {
        this.showBackdrop = true;
        const foundStep = this.steps.find(
          (step) => step.stepCount === response
        );
        if (foundStep) {
          this.currentStep = foundStep;
          this.guidedTourStep();
        }
      }
    });
  }

  initializeSteps(): void {
    this.steps = this.constants.Steps.map((step, index) => ({
      ...step,
      stepCount: index,
    }));
    this.totalStepCount = this.steps.length;
  }

  guidedTourStep(): void {
    const step = this.currentStep;
    const overlay = document.getElementById(this.constants.OverlayId);
    const isLastStep = step.stepCount === this.totalStepCount - 1;

    if (window.innerWidth <= 768) {
      const target = document.getElementById(step.selector);
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      if(isLastStep){
        window.scrollTo({top:0, behavior:"smooth"})
      }
    }

    switch (step.stepCount) {
      case 0:
        this.modalService.changeTab("measures");
        break;
      case 1:
        this.modalService.changeTab("interventions");
        break;
      case 2:
        this.modalService.changeTab("behaviors");
        break;
      default:
        this.modalService.changeTab("clear");
        break;
    }

    if (step.stepCount === 4) {
      this.tourService.triggerFilterStep();
    }

    if (step.stepCount === 5) {
      this.tourService.triggerFilterStepOff();
    }

    this.appendOverlay(step, overlay, isLastStep);

    const shouldHighlight = step.stepCount > 2;
    this.guidedTourHighlightElement(shouldHighlight);
  }

  appendOverlay(step, overlay: HTMLElement, isLastStep: boolean): void {
    if (!overlay) return;

    const isMobile = window.innerWidth <= 768;

    const dynamicTop = step.mobileTop || "50%";

    if (isLastStep || isMobile) {
      overlay.setAttribute(
        "style",
        `
        position: fixed;
        top: ${dynamicTop};
        left: 50%;
        transform: translate(-50%, 0);
        z-index: 9999;
      `
      );
      if (overlay.parentElement) {
        overlay.parentElement.removeChild(overlay);
      }
      document.body.appendChild(overlay);
      return;
    }

    const target = document.getElementById(step.selector);
    if (!target) {
      console.warn(
        `Target element '${step.selector}' not found. Aborting step.`
      );
      return;
    }

    overlay.setAttribute(
      "style",
      `
      position: absolute;
      right: ${step.right};
      top: ${step.top};
      left: ${step.left};
      z-index: 9999;
    `
    );
    target.appendChild(overlay);
  }

  guidedTourHighlightElement(highlight: boolean): void {
    const element = document.getElementById(this.currentStep.selector);
    if (!element) {
      console.warn(`Target element '${this.currentStep.selector}' not found.`);
      return;
    }
    console.log("HIGHLIGHTING ELEMENT-------------------", element)
    element.classList.toggle("highlight-element", highlight);
  }

  endTour(): void {
    this.guidedTourHighlightElement(false);
    this.tourService.setStartTour(false);
    this.tourService.setCurrentStep(-1);
    this.currentStep = this.constants.NullStep;
    this.showBackdrop = false;
    localStorage.setItem("did_user_close_tour", "yes");
  }

  goToStep(stepNumber: number): void {
    this.guidedTourHighlightElement(false);
    this.tourService.setCurrentStep(stepNumber);
  }

  goToNext(): void {
    console.log("STEP NEXT", this.currentStep.stepCount);
    this.guidedTourHighlightElement(false);
    if (this.currentStep.stepCount === this.totalStepCount - 1) {
      this.endTour();
    } else {
      this.tourService.setCurrentStep(this.currentStep.stepCount + 1);
    }
  }

  goToPrevious(): void {
    this.guidedTourHighlightElement(false);

    console.log("STEP BACK", this.currentStep.stepCount);

    if (this.currentStep.stepCount === 4) {
      this.tourService.triggerFilterStep();
    }

    if (this.currentStep.stepCount === this.totalStepCount - 1) {
      this.endTour();
    } else if (this.currentStep.stepCount === 0) {
      this.endTour();
      this.modalService.triggerShowHeaderModal();
    } else {
      this.tourService.setCurrentStep(this.currentStep.stepCount - 1);
    }
  }

  restartTour(): void {
    this.endTour();
    this.modalService.triggerShowHeaderModal();
  }

  ngOnDestroy(): void {
    // unsubscribing from the subscriptions used in code
    this.$currentStep ? this.$currentStep.unsubscribe() : null;
    this.$guidedTourCompleted ? this.$guidedTourCompleted.unsubscribe() : null;
  }
  onOverlayClick(event: MouseEvent) {
    if (event.target === event.currentTarget) {
      this.endTour();
    }
  }
}
